import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Instagram, Youtube, Mail, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { useDialogs } from "@/components/GlobalDialogs";
import { submitInspiration } from "@/lib/api";

export const Footer = () => {
  const { openJoin, openWaitlist } = useDialogs();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await submitInspiration({ email });
      toast.success(res.message);
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-border bg-[#141414] text-white noise">
      <div className="ytca-container relative py-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-3xl font-semibold">YTCA</span>
              <span className="h-2 w-2 rounded-full bg-primary" />
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/70">
              You Too Can Art is a movement built on one belief: creativity is inherent to every
              human being, and art belongs in everyday life.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex max-w-sm gap-2">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for daily inspiration"
                data-testid="footer-inspiration-email-input"
                className="bg-white/10 text-white placeholder:text-white/50 border-white/20"
              />
              <Button type="submit" disabled={loading} data-testid="footer-inspiration-submit" className="rounded-full shrink-0">
                {loading ? "..." : <ArrowRight className="h-4 w-4" />}
              </Button>
            </form>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50">Explore</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li><Link to="/about" className="text-white/80 hover:text-white" data-testid="footer-link-about">About the Movement</Link></li>
              <li><Link to="/events" className="text-white/80 hover:text-white" data-testid="footer-link-events">Classes & Sessions</Link></li>
              <li><Link to="/blog" className="text-white/80 hover:text-white" data-testid="footer-link-projects">Our Work (on the Blog)</Link></li>
              <li><Link to="/learn-earn" className="text-white/80 hover:text-white" data-testid="footer-link-learn">Learn & Earn</Link></li>
              <li><Link to="/studio" className="text-white/80 hover:text-white" data-testid="footer-link-studio">YTCA Studio</Link></li>
              <li><Link to="/blog" className="text-white/80 hover:text-white" data-testid="footer-link-blog">Blog</Link></li>
              <li><Link to="/my-enrolments" className="text-white/80 hover:text-white" data-testid="footer-link-my-enrolments">My Enrolments</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider text-white/50">Be Part of It</h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li><button onClick={openJoin} className="text-white/80 hover:text-white" data-testid="footer-join-button">Join YTCA</button></li>
              <li><button onClick={() => openWaitlist()} className="text-white/80 hover:text-white" data-testid="footer-waitlist-button">Join the Waitlist</button></li>
              <li><Link to="/collaborate" className="text-white/80 hover:text-white" data-testid="footer-collaborate-button">Collaborate With Us</Link></li>
              <li><Link to="/contact" className="text-white/80 hover:text-white" data-testid="footer-link-contact">Contact</Link></li>
            </ul>
            <div className="mt-6 flex gap-3">
              <a href="#" aria-label="Instagram" className="rounded-full border border-white/20 p-2 text-white/80 hover:text-white"><Instagram className="h-4 w-4" /></a>
              <a href="#" aria-label="YouTube" className="rounded-full border border-white/20 p-2 text-white/80 hover:text-white"><Youtube className="h-4 w-4" /></a>
              <a href="mailto:hello@ytca.art" aria-label="Email" className="rounded-full border border-white/20 p-2 text-white/80 hover:text-white"><Mail className="h-4 w-4" /></a>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row sm:items-center">
          <span>© {new Date().getFullYear()} YTCA — You Too Can Art. A creative community.</span>
          <span>Made with intention, not perfection.</span>
        </div>
      </div>
    </footer>
  );
};
